import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import styles from '../../styles/Dialog.module.css';

const TokenGeneratedModal = ({ isOpen, onClose, token, studentName }) => {
  const { isDarkMode } = useTheme();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  // Use local state to ensure modal stays visible even if parent unmounts
  const [localIsOpen, setLocalIsOpen] = useState(isOpen);
  const [localToken, setLocalToken] = useState(token);
  const [localStudentName, setLocalStudentName] = useState(studentName);
  
  // Update local state when props change
  useEffect(() => {
    console.log('TokenGeneratedModal props changed:', { isOpen, token, studentName });
    if (isOpen && token) {
      setLocalIsOpen(true);
      setLocalToken(token);
      setLocalStudentName(studentName);
    }
  }, [isOpen, token, studentName]);

  // Debug log render conditions with more details
  console.log('TokenGeneratedModal render attempt:', {
    isOpen,
    localIsOpen,
    hasToken: Boolean(token),
    hasLocalToken: Boolean(localToken),
    tokenValue: token,
    localTokenValue: localToken,
    isAuthenticated,
    studentName,
    localStudentName,
    stack: new Error().stack
  });

  // Close if conditions not met
  if (!localIsOpen || !localToken) {
    console.log('TokenGeneratedModal render skipped:', {
      notOpen: !localIsOpen,
      noToken: !localToken,
      isAuthenticated,
      stack: new Error().stack
    });
    return null;
  }

  // Log successful render
  console.log('TokenGeneratedModal rendering with token:', localToken);

  const handleCopyToken = async () => {
    try {
      console.log('Copying token to clipboard:', localToken);
      await navigator.clipboard.writeText(localToken);
      alert('Token copiado al portapapeles');
    } catch (err) {
      console.error('Error al copiar el token:', err);
    }
  };

  const handleContinue = () => {
    console.log('Continue button clicked, closing token modal');
    setLocalIsOpen(false); // Update local state first
    if (onClose) {
      console.log('Calling onClose callback');
      onClose();
    }
    console.log('Navigating to home page');
    navigate('/');
  };

  // Set CSS variables for theme colors
  const cssVariables = {
    '--bg-color': isDarkMode ? '#000' : '#fff',
    '--border-color': isDarkMode ? '#fff' : '#000',
    '--text-color': isDarkMode ? '#fff' : '#000',
    '--section-bg': isDarkMode ? '#333' : '#f0f0f0'
  };

  const tokenStyle = {
    backgroundColor: isDarkMode ? '#333' : '#f0f0f0',
    padding: '1rem',
    margin: '1rem 0',
    fontSize: '2rem',
    fontWeight: 'bold',
    letterSpacing: '0.25rem',
    fontFamily: 'monospace',
    userSelect: 'all',
    cursor: 'copy'
  };

  return (
    <div
      className={styles.overlay}
      style={{
        ...cssVariables,
        zIndex: 2000 // Higher than the purchase modal's z-index
      }}>
      <div className={styles.dialog} onClick={e => e.stopPropagation()}>
        <h2 className={styles.title}>
          ¡TOKEN GENERADO CON ÉXITO!
        </h2>

        <p style={{ marginBottom: '1rem' }}>
          Hola {localStudentName}, aquí tienes tu token de acceso:
        </p>

        <div style={tokenStyle} onClick={handleCopyToken}>
          {localToken}
        </div>

        <p style={{ 
          fontSize: '0.9rem',
          color: isDarkMode ? '#ccc' : '#666',
          marginBottom: '1rem'
        }}>
          (Haz clic en el token para copiarlo)
        </p>

        <div className={styles.section}>
          <h3 style={{
            fontSize: '1rem',
            marginBottom: '0.5rem',
            color: '#FF9800'
          }}>
            ⚠️ IMPORTANTE
          </h3>
          <ul className={styles.featureList}>
            <li style={{ marginBottom: '0.5rem' }}>
              • Guarda este token en un lugar seguro
            </li>
            <li style={{ marginBottom: '0.5rem' }}>
              • Lo necesitarás para acceder a los tests
            </li>
            <li>
              • No compartas tu token con nadie
            </li>
          </ul>
        </div>

        <button
          onClick={handleContinue}
          className={styles.primaryButton}
        >
          ENTENDIDO
        </button>
      </div>
    </div>
  );
};

export default TokenGeneratedModal;